import { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';

import { useChatSupport } from 'contexts/chatSupport';
import { useBillingMultipliers } from 'hooks/backend/billing';
import { pricesQueries } from 'hooks/backend/prices';
import { ReactSelectValue } from 'types/forms';
import { RateTierService } from 'types/domainModels';

import Icon from 'components/common/Icon';
import Logo from 'components/common/Logo';
import Button from 'components/common/forms/Button';

import Stepper from 'components/DesignSystem/Stepper/Stepper';
import Summary from './Summary/Summary';
import SurveyTypePanel, {
  DIY,
  FULL_SERVICE,
  HYBRID,
} from './SurveyTypePanel/SurveyTypePanel';
import SampleDetailsPanel, {
  INCIDENCE_OPTIONS,
} from './SampleDetailsPanel/SampleDetailsPanel';
import SurveyOptionsPanel from './SurveyOptionsPanel/SurveyOptionsPanel';
import ReportingOptionsPanel from './ReportingOptionsPanel/ReportingOptionsPanel';
import QuoteSummaryPanel from './QuoteSummaryPanel/QuoteSummaryPanel';
import { BillingPrice, BillingServiceName } from 'services/backend/billing';
import { getOptionPrice } from './helpers';
import {
  formatDollars,
  removeSpecialCharFromCurrencyString,
} from 'util/currency';

const DEFAULT_QUOTE_STEPS = [
  'Survey Type',
  'Sample Details',
  'Survey Options',
  'Reporting Options',
  'Quote Summary',
];

const DIY_QUOTE_STEPS = ['Survey Type', 'Sample Details', 'Quote Summary'];

const CREATION_QUESTION_COUNT = 'CREATION_QUESTION_COUNT';
const METHODOLOGY_COMPLEXITY = 'METHODOLOGY_COMPLEXITY';
const FIELD_MANAGEMENT_GROUPS = 'FIELD_MANAGEMENT_GROUPS';
const REPORTING_COMPLEXITY = 'REPORTING_COMPLEXITY';
const REPORTING_GROUPS = 'REPORTING_GROUPS';

const METHODOLOGY_APPROACH_CONSULTATION = 'METHODOLOGY_APPROACH_CONSULTATION';
const QUESTIONNAIRE_DESIGN = 'QUESTIONNAIRE_DESIGN';
const SURVEY_PROGRAMMING = 'SURVEY_PROGRAMMING';
const SURVEY_REVIEW = 'SURVEY_REVIEW';
const FIELD_MANAGEMENT = 'FIELD_MANAGEMENT';

const TABLE_CREATION = 'TABLE_CREATION';
const SCORECARD = 'SCORECARD';
const FOCUSED_REPORT = 'FOCUSED_REPORT';
const SIGNATURE_DECK = 'SIGNATURE_DECK';
const EXTENDED_DECK = 'EXTENDED_DECK';

export const QuoteTool = ({
  isOpen,
  onCloseModal,
}: {
  isOpen: boolean;
  onCloseModal(): void;
}): JSX.Element => {
  const { showChat } = useChatSupport();

  const { data: multipliers } = useBillingMultipliers(undefined, {
    enabled: isOpen,
  });

  const activeMultipliers = useMemo(
    () => multipliers?.filter((mult) => mult.isActive),
    [multipliers],
  );

  const numberOfQuestionsOptions = activeMultipliers
    ?.find((mult) => mult.name === CREATION_QUESTION_COUNT)
    ?.options.map((option) => ({
      label: option.name,
      value: String(option.id),
      isDefault: option.isDefault,
    }));
  const methodologyComplexityOptions = activeMultipliers
    ?.find((mult) => mult.name === METHODOLOGY_COMPLEXITY)
    ?.options.map((option) => ({
      label: option.name,
      value: String(option.id),
      isDefault: option.isDefault,
    }));
  const metholologyFieldworkGroupsOptions = activeMultipliers
    ?.find((mult) => mult.name === FIELD_MANAGEMENT_GROUPS)
    ?.options.map((option) => ({
      label: option.name,
      value: String(option.id),
      isDefault: option.isDefault,
    }));
  const reportComplexityOptions = activeMultipliers
    ?.find((mult) => mult.name === REPORTING_COMPLEXITY)
    ?.options.map((option) => ({
      label: option.name,
      value: String(option.id),
      isDefault: option.isDefault,
    }));
  const reportingGroupsOptions = activeMultipliers
    ?.find((mult) => mult.name === REPORTING_GROUPS)
    ?.options.map((option) => ({
      label: option.name,
      value: String(option.id),
      isDefault: option.isDefault,
    }));

  // survey type
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [selectedPanel, setSelectedPanel] = useState(DEFAULT_QUOTE_STEPS[0]);
  const [selectedSurveyType, setSelectedSurveyType] = useState('');
  const [surveyTotal, setSurveyTotal] = useState(0);
  // survey details
  const [numberOfRespondents, setNumberOfRespondents] = useState<
    number | undefined
  >(undefined);
  const [estimatedIncidence, setEstimatedIncidence] =
    useState<ReactSelectValue<string> | null>(null);
  const [numberOfQuestions, setNumberOfQuestions] =
    useState<ReactSelectValue<string> | null>(null);
  const [baseSubtotal, setBaseSubtotal] = useState(0);
  const [
    surveyDesignAndFieldingMethodology,
    setSurveyDesignAndFieldingMethodology,
  ] = useState(false);
  const [
    surveyDesignAndFieldingQuestionnaire,
    setSurveyDesignAndFieldingQuestionnaire,
  ] = useState(false);
  const [
    surveyDesignAndFieldingProgramming,
    setSurveyDesignAndFieldingProgramming,
  ] = useState(false);
  const [surveyDesignAndFieldingReview, setSurveyDesignAndFieldingReview] =
    useState(false);
  const [
    surveyDesignAndFieldingManagement,
    setSurveyDesignAndFieldingManagement,
  ] = useState(false);
  const [servicesSubtotal, setServicesSubtotal] = useState(0);
  const [methodologyComplexity, setMethodologyComplexity] = useState('');
  const [methodologyFieldworkGroups, setMethodologyFieldworkGroups] =
    useState('');
  const [reportingAndAnalysis, setReportingAndAnalysis] = useState('');
  // reporting and analysis
  const [reportingTableCreation, setReportingTableCreation] = useState(false);
  const [reportingScorecard, setReportingScorecard] = useState(false);
  const [reportingFocusedReport, setReportingFocusedReport] = useState(false);
  const [reportingSignatureDeck, setReportingSignatureDeck] = useState(false);
  const [reportingExtendedDeck, setReportingExtendedDeck] = useState(false);
  const [reportingSubtotal, setReportingSubtotal] = useState(0);
  const [numberOfGroups, setNumberOfGroups] = useState('');
  const [reportComplexity, setReportComplexity] = useState('');
  // chunks of data
  const [allActivePrices, setAllActivePrices] = useState<
    BillingPrice[] | undefined
  >();

  const handleModalClose = () => {
    setSelectedPanel(DEFAULT_QUOTE_STEPS[0]);
    setSelectedSurveyType('');
    resetFormExceptType();
    onCloseModal();
  };

  const resetFormExceptType = () => {
    setNumberOfRespondents(undefined);
    setEstimatedIncidence(INCIDENCE_OPTIONS[0]);
    setNumberOfQuestions(
      numberOfQuestionsOptions?.find((option) => option.isDefault) ?? null,
    );
    setSurveyDesignAndFieldingMethodology(false);
    setSurveyDesignAndFieldingQuestionnaire(false);
    setSurveyDesignAndFieldingProgramming(false);
    setSurveyDesignAndFieldingReview(false);
    setSurveyDesignAndFieldingManagement(false);
    setMethodologyComplexity(
      methodologyComplexityOptions?.find((option) => option.isDefault)?.value ??
        '',
    );
    setMethodologyFieldworkGroups(
      metholologyFieldworkGroupsOptions?.find((option) => option.isDefault)
        ?.value ?? '',
    );
    setReportingAndAnalysis('');
    setReportingTableCreation(false);
    setReportingScorecard(false);
    setReportingFocusedReport(false);
    setReportingSignatureDeck(false);
    setReportingExtendedDeck(false);
    setNumberOfGroups(
      reportingGroupsOptions?.find((option) => option.isDefault)?.value ?? '',
    );
    setReportComplexity(
      reportComplexityOptions?.find((option) => option.isDefault)?.value ?? '',
    );
    setSurveyTotal(0);
    setBaseSubtotal(0);
    setServicesSubtotal(0);
    setReportingSubtotal(0);
  };

  useEffect(() => {
    resetFormExceptType();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSurveyType]);

  useEffect(() => {
    scrollContainerRef.current?.scrollTo(0, 0);
  }, [selectedPanel]);

  const quoteServices: RateTierService[] = [
    {
      flag: selectedSurveyType === FULL_SERVICE,
      service: 'full_service_strategy' as const,
    },
    {
      flag: surveyDesignAndFieldingMethodology,
      service: 'methodology_and_approach_consultation' as const,
    },
    {
      flag: surveyDesignAndFieldingQuestionnaire,
      service: 'questionnaire_design' as const,
    },
    {
      flag: surveyDesignAndFieldingProgramming,
      service: 'survey_programming' as const,
    },
    {
      flag: surveyDesignAndFieldingReview,
      service: 'survey_review' as const,
    },
    {
      flag: surveyDesignAndFieldingManagement,
      service: 'survey_field_management' as const,
    },
    { flag: reportingTableCreation, service: 'table_creation' as const },
    { flag: reportingScorecard, service: 'scorecard' as const },
    { flag: reportingFocusedReport, service: 'focused_report' as const },
    { flag: reportingSignatureDeck, service: 'signature_deck' as const },
    { flag: reportingExtendedDeck, service: 'extended_deck' as const },
  ]
    .filter(({ flag }) => flag)
    .map(({ service }) => service);

  const selectedMultiplierOptions = [
    methodologyComplexity,
    methodologyFieldworkGroups,
    reportComplexity,
    numberOfGroups,
  ]
    .filter((value) => value !== null)
    .map(Number);

  const { data: quote, isLoading: isLoadingQuote } = useQuery(
    pricesQueries.getQuote({
      incidence: Number(estimatedIncidence?.value),
      questionCount: numberOfQuestions ? Number(numberOfQuestions.value) : null,
      respondentsCount: numberOfRespondents ?? 0,
      services: quoteServices,
      multiplierOptions: selectedMultiplierOptions,
    }),
  );

  const { data: priceBreakdown, isLoading: isLoadingBreakdown } = useQuery(
    pricesQueries.getPriceBreakdown({
      incidence: Number(estimatedIncidence?.value),
      questionCount: numberOfQuestions ? Number(numberOfQuestions.value) : null,
      respondentsCount: numberOfRespondents ?? 0,
      multiplierOptions: selectedMultiplierOptions,
    }),
  );

  useEffect(() => {
    updateServicePrices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceBreakdown]);

  const updateServicePrices = () => {
    if (!priceBreakdown) {return;}

    const newPrices = Object.entries(priceBreakdown).map(
      ([priceName, price]) => ({
        priceName,
        price: formatDollars(price / 100),
      }),
    );
    setBaseSubtotal(priceBreakdown.BASE_PRICE / 100);
    updateSubtotals(newPrices);
    setAllActivePrices(newPrices);
  };

  const updateSubtotals = (newPrices = allActivePrices) => {
    const TOTAL_INITIAL_VALUE = 0;

    const servicesSubtotal = surveyDetailsData
      .filter((detail) => detail.isSelected)
      .map((detail) => {
        const price = getOptionPrice(detail.value, newPrices);
        return removeSpecialCharFromCurrencyString(price!);
      })
      .reduce((total, current) => +total + +current, TOTAL_INITIAL_VALUE);
    const reportingSubtotal = reportingOptionsData
      .filter((detail) => detail.isSelected)
      .map((detail) => {
        const price = getOptionPrice(detail.value, newPrices);
        return removeSpecialCharFromCurrencyString(price!);
      })
      .reduce((total, current) => +total + +current, TOTAL_INITIAL_VALUE);
    setServicesSubtotal(servicesSubtotal);
    setReportingSubtotal(reportingSubtotal);
  };

  const updateQuote = () => {
    updateSubtotals();
    setSurveyTotal(quote ? quote?.total / 100 : 0);
  };

  useEffect(() => {
    updateQuote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote]);

  const surveyDetailsData: {
    ordering: number;
    label: string;
    value: BillingServiceName;
    tooltip: string;
    subtitle: string;
    isSelected: boolean;
    onToggle: () => void;
  }[] = [
    {
      ordering: 1,
      label: 'Methodology & Approach Consultation',
      value: METHODOLOGY_APPROACH_CONSULTATION,
      tooltip:
        "Up to an hour of time from a Glass research expert, who will work with you to identify the survey approach, methodology, audience best suited to answer your research goals.  This can happen at the start of a project, or to help review a questionnaire you've started to design.",
      subtitle: 'Expert guidance on study design and research approach',
      isSelected: surveyDesignAndFieldingMethodology,
      onToggle: () =>
        setSurveyDesignAndFieldingMethodology((isSelected) => !isSelected),
    },
    {
      ordering: 2,
      label: 'Questionnaire Design',
      value: QUESTIONNAIRE_DESIGN,
      tooltip:
        'A Glass research expert will work with you to understand your goals, then craft a custom questionnaire that utilizes best practice methodology and design approach to meet your needs.  Questionnaire will be created on paper (not yet programmed into the Glass platform) and will include all details necessary to program into a survey (wording, question options, survey logic).',
      subtitle: 'Professional survey writing and question development',
      isSelected: surveyDesignAndFieldingQuestionnaire,
      onToggle: () =>
        setSurveyDesignAndFieldingQuestionnaire((isSelected) => !isSelected),
    },
    {
      ordering: 3,
      label: 'Survey Programming',
      value: SURVEY_PROGRAMMING,
      tooltip:
        'Glass will take your written-out questionnaire and program it into a survey on the Glass platform.  Includes quality checks to ensure survey logic is working properly.',
      subtitle: 'Technical build and logic configuration of your survey',
      isSelected: surveyDesignAndFieldingProgramming,
      onToggle: () =>
        setSurveyDesignAndFieldingProgramming((isSelected) => !isSelected),
    },
    {
      ordering: 4,
      label: 'Survey Review',
      value: SURVEY_REVIEW,
      tooltip:
        'Want to program your own survey, but with some support?  A Glass researcher will review your programmed survey to ensure it will operate as intended and share recommendations for best outcome.',
      subtitle: 'Quality review and optimization before launch',
      isSelected: surveyDesignAndFieldingReview,
      onToggle: () =>
        setSurveyDesignAndFieldingReview((isSelected) => !isSelected),
    },
    {
      ordering: 5,
      label: 'Survey Field Management',
      value: FIELD_MANAGEMENT,
      tooltip:
        'Glass will design and execute a sample and field approach to best meet your sample needs.  Includes field management and a round of final data cleaning.',
      subtitle: 'Active monitoring and management during fielding',
      isSelected: surveyDesignAndFieldingManagement,
      onToggle: () =>
        setSurveyDesignAndFieldingManagement((isSelected) => !isSelected),
    },
  ];

  const reportingOptionsData: {
    ordering: number;
    label: string;
    value: BillingServiceName;
    tooltip: string;
    subtitle: string;
    isSelected: boolean;
    onToggle: () => void;
  }[] = [
    {
      ordering: 1,
      label: 'Table Creation',
      value: TABLE_CREATION,
      tooltip:
        'Creation of 1-10 custom banners and resulting crosstabs, including stat testing.',
      subtitle: 'Cross-tabulated data tables for your results',
      isSelected: reportingTableCreation,
      onToggle: () => setReportingTableCreation((isSelected) => !isSelected),
    },
    {
      ordering: 2,
      label: 'Scorecard',
      value: SCORECARD,
      tooltip:
        'Roughly 3 summary slides or Excel worksheets summarizing key metrics and highest level takeaways.',
      subtitle: 'Concise performance summary with key metrics',
      isSelected: reportingScorecard,
      onToggle: () => setReportingScorecard((isSelected) => !isSelected),
    },
    {
      ordering: 3,
      label: 'Focused Report',
      value: FOCUSED_REPORT,
      tooltip:
        'A focused deliverable sharing "what matters" in roughly 10 slides.  This deliverable will focus on 3-5 key learnings or objectives in an easy to understand format.',
      subtitle: 'Targeted insights report for specific research objectives',
      isSelected: reportingFocusedReport,
      onToggle: () => setReportingFocusedReport((isSelected) => !isSelected),
    },
    {
      ordering: 4,
      label: 'Signature Deck',
      value: SIGNATURE_DECK,
      tooltip:
        'Our signature deliverable is a story-driven deck that is generally about 20 slides.  It focuses on the objectives for the study, including call-outs and recommendations based on the findings.',
      subtitle: 'Polished presentation with branded visuals and narrative',
      isSelected: reportingSignatureDeck,
      onToggle: () => setReportingSignatureDeck((isSelected) => !isSelected),
    },
    {
      ordering: 5,
      label: 'Extended Deck',
      value: EXTENDED_DECK,
      tooltip:
        'The extended deck is story-driven, with topical deep-dives into sub-groups, or sub-topics of interest.  Generally 30+ slides and a comprehensive appendix.  This report option is generally used for multi-category, or mixed-methods projects.',
      subtitle: 'Comprehensive analysis deck with deep-dive insights',
      isSelected: reportingExtendedDeck,
      onToggle: () => setReportingExtendedDeck((isSelected) => !isSelected),
    },
  ];

  const allRequiredOptionsChosenForPage = (() => {
    switch (selectedPanel) {
      case DEFAULT_QUOTE_STEPS[0]: // Survey Type
        if (selectedSurveyType) {
          return true;
        }
        return false;
      case DEFAULT_QUOTE_STEPS[1]: // Survey Details
        if (numberOfRespondents && estimatedIncidence && numberOfQuestions) {
          return true;
        }
        return false;
      case DEFAULT_QUOTE_STEPS[2]: // Survey Options
        if (
          selectedSurveyType === FULL_SERVICE &&
          methodologyComplexity &&
          methodologyFieldworkGroups
        ) {
          return true;
        }
        if (
          selectedSurveyType === HYBRID &&
          methodologyComplexity &&
          methodologyFieldworkGroups &&
          reportingAndAnalysis
        ) {
          return true;
        }
        return false;
      case DEFAULT_QUOTE_STEPS[3]: // Reporting Options
        if (numberOfGroups && reportComplexity) {
          return true;
        }
        return false;
      case DEFAULT_QUOTE_STEPS[4]: // Quote Summary
        return true;
      default:
        return false;
    }
  })();

  const QuotePage = () => {
    switch (selectedPanel) {
      case DEFAULT_QUOTE_STEPS[0]: // Survey Type
        return (
          <SurveyTypePanel
            selectedSurveyType={selectedSurveyType}
            setSelectedSurveyType={setSelectedSurveyType}
          />
        );
      case DEFAULT_QUOTE_STEPS[1]: // Sample Details
        return (
          <SampleDetailsPanel
            estimatedIncidence={estimatedIncidence}
            numberOfQuestions={numberOfQuestions}
            numberOfQuestionsOptions={numberOfQuestionsOptions}
            numberOfRespondents={numberOfRespondents}
            setEstimatedIncidence={setEstimatedIncidence}
            setNumberOfQuestions={setNumberOfQuestions}
            setNumberOfRespondents={setNumberOfRespondents}
            surveyType={selectedSurveyType}
          />
        );
      case DEFAULT_QUOTE_STEPS[2]: // Survey Options
        return (
          <SurveyOptionsPanel
            activePrices={allActivePrices}
            isLoadingBreakdown={isLoadingBreakdown}
            methodologyComplexity={methodologyComplexity}
            methodologyComplexityOptions={methodologyComplexityOptions}
            methodologyFieldworkGroups={methodologyFieldworkGroups}
            metholologyFieldworkGroupsOptions={
              metholologyFieldworkGroupsOptions
            }
            reportingAndAnalysis={reportingAndAnalysis}
            setMethodologyComplexity={setMethodologyComplexity}
            setMethodologyFieldworkGroups={setMethodologyFieldworkGroups}
            setReportingAndAnalysis={setReportingAndAnalysis}
            surveyDetailsData={surveyDetailsData}
            surveyType={selectedSurveyType}
          />
        );
      case DEFAULT_QUOTE_STEPS[3]: // Reporting Options
        return (
          <ReportingOptionsPanel
            activePrices={allActivePrices}
            isLoadingBreakdown={isLoadingBreakdown}
            numberOfGroups={numberOfGroups}
            reportComplexity={reportComplexity}
            reportComplexityOptions={reportComplexityOptions}
            reportingGroupsOptions={reportingGroupsOptions}
            reportingOptionsData={reportingOptionsData}
            setNumberOfGroups={setNumberOfGroups}
            setReportComplexity={setReportComplexity}
          />
        );
      case DEFAULT_QUOTE_STEPS[4]: // Quote Summary
        return (
          <QuoteSummaryPanel
            activePrices={allActivePrices}
            baseSubtotal={baseSubtotal}
            estimatedIncidence={estimatedIncidence}
            methodologyComplexity={methodologyComplexity}
            methodologyComplexityOptions={methodologyComplexityOptions}
            methodologyFieldworkGroups={methodologyFieldworkGroups}
            metholologyFieldworkGroupsOptions={
              metholologyFieldworkGroupsOptions
            }
            numberOfGroups={numberOfGroups}
            numberOfQuestions={numberOfQuestions}
            numberOfRespondents={numberOfRespondents}
            reportComplexity={reportComplexity}
            reportComplexityOptions={reportComplexityOptions}
            reportingGroupsOptions={reportingGroupsOptions}
            reportingOptionsData={reportingOptionsData}
            reportingSubtotal={reportingSubtotal}
            servicesSubtotal={servicesSubtotal}
            surveyDetailsData={surveyDetailsData}
            surveyTotal={surveyTotal}
            surveyType={selectedSurveyType}
          />
        );
      default:
        return false;
    }
  };

  const selectedSurveySteps =
    selectedSurveyType === DIY ||
    (selectedSurveyType === HYBRID && reportingAndAnalysis === 'No')
      ? DIY_QUOTE_STEPS
      : DEFAULT_QUOTE_STEPS;
  const activeIndex = selectedSurveySteps.indexOf(selectedPanel);
  const onLastPage = activeIndex === selectedSurveySteps.length - 1;

  const goToPreviousPage = () => {
    setSelectedPanel(selectedSurveySteps[activeIndex - 1]);
  };

  const goToNextPage = () => {
    setSelectedPanel(selectedSurveySteps[activeIndex + 1]);
  };

  return (
    <Dialog
      onClose={() => {
        handleModalClose();
      }}
      open={isOpen}
    >
      <Dialog.Panel className="bg-[#F5F7F6] fixed inset-0 w-screen h-screen z-30 flex flex-col font-inter font-light">
        <div className="sticky top-0 w-full h-[75px] shrink-0 bg-white flex justify-between items-center px-6 z-10">
          <Logo />
          <div
            className="w-5 h-5 text-dark-grey cursor-pointer"
            onClick={handleModalClose}
          >
            <Icon id="x" />
          </div>
        </div>
        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto pb-6">
          <div className="w-1/2 mx-auto py-8">
            <Stepper
              activeStep={selectedPanel}
              onClick={(index) => {
                setSelectedPanel(selectedSurveySteps[index]);
              }}
              stepTitles={selectedSurveySteps}
            />
          </div>
          <div className="flex w-2/3 justify-center mx-auto gap-4">
            {QuotePage()}
            {selectedPanel !== 'Quote Summary' && (
              <Summary
                activePrices={allActivePrices}
                baseSubtotal={baseSubtotal}
                estimatedIncidence={estimatedIncidence}
                isLoadingQuote={isLoadingQuote}
                methodologyComplexity={methodologyComplexity}
                methodologyComplexityOptions={methodologyComplexityOptions}
                methodologyFieldworkGroups={methodologyFieldworkGroups}
                metholologyFieldworkGroupsOptions={
                  metholologyFieldworkGroupsOptions
                }
                numberOfGroups={numberOfGroups}
                numberOfQuestions={numberOfQuestions}
                numberOfRespondents={numberOfRespondents}
                reportComplexity={reportComplexity}
                reportComplexityOptions={reportComplexityOptions}
                reportingGroupsOptions={reportingGroupsOptions}
                reportingOptionsData={reportingOptionsData}
                reportingSubtotal={reportingSubtotal}
                servicesSubtotal={servicesSubtotal}
                surveyDetailsData={surveyDetailsData}
                surveyTotal={surveyTotal}
                surveyType={selectedSurveyType}
              />
            )}
          </div>
        </div>
        <div className="sticky bottom-0 w-full h-[75px] shrink-0 bg-white flex justify-between items-center px-48 z-10">
          <Button
            disabled={activeIndex === 0}
            hierarchy="secondary-gray"
            icon={<Icon id="arrow-narrow-left" />}
            iconPlacement="leading"
            onClick={goToPreviousPage}
            size="md"
          >
            Back
          </Button>
          <div>
            <span className="text-gray-500">Need help?</span>{' '}
            <span className="cursor-pointer" onClick={showChat}>
              Chat with us
            </span>
          </div>
          <Button
            disabled={!allRequiredOptionsChosenForPage}
            hierarchy={'primary'}
            icon={
              onLastPage ? (
                <Icon id="x" />
              ) : (
                <span className="inline-block rotate-180">
                  <Icon id="arrow-narrow-left" />
                </span>
              )
            }
            iconPlacement="trailing"
            onClick={onLastPage ? handleModalClose : goToNextPage}
            size="md"
          >
            {onLastPage ? 'Quote Complete - Close' : 'Continue'}
          </Button>
        </div>
      </Dialog.Panel>
    </Dialog>
  );
};
